import { z } from "zod";

import { jurisdictionProfileSchema } from "../services/cableContract";
import {
  generatorDutyClassSchema,
  generatorRedundancyModeSchema,
} from "../services/generatorSizingContract";
import { loadScenarioSchema } from "../services/loadDemandContract";
import { deriveStandbyUnits } from "../utils/sourceSizing";
import type { ValidationLabels } from "../utils/validationMessages";

// Draft model of the generator (DG) sizing study form (EOS-03b): what the user
// is typing, kept apart from the components so the payload rules are tested
// without a browser.
//
// Every value is held as the string the user typed, including the unit counts.
// A blank optional field is left OUT of the payload: under Master Prompt
// A16 (a) a blank factor means NOT ESTABLISHED, and the engine must see it as
// absent, never as "" and never as "1". The same holds for the step load under
// A17: blank is NOT ESTABLISHED, while an entered "0" states there is none and
// is sent as "0". The power factor is an engineering input, not a margin, so it
// stays required.
//
// Duty class, redundancy mode and scenario have no default in the backend
// (A17), so the draft starts with none of them selected ("") and a blank choice
// is left out for the schema to report as required.
//
// The available unit ratings are separate entries with stable ids, so adding or
// removing one never disturbs the others; the ids never reach the backend.
// Every entry is sent in the order it is on screen, blank ones included - the
// backend requires a unique ascending schedule of positive decimals and must be
// the one to say so, entry by entry.

export type GeneratorRatingDraft = {
  id: string;
  value: string;
};

export type GeneratorStudyDraft = {
  code: string;
  name: string;
  steadyStateDemandKw: string;
  steadyStatePowerFactor: string;
  transientStepLoadKva: string;
  transientAllowanceFactor: string;
  futureGrowthFactor: string;
  designMarginFactor: string;
  ambientDeratingFactor: string;
  altitudeDeratingFactor: string;
  dutyUnits: string;
  // Derived from the redundancy mode and the duty units; shown, never typed.
  standbyUnits: string;
  dutyClass: string;
  redundancyMode: string;
  scenario: string;
  jurisdictionProfile: string;
  notes: string;
  /** The source picked from the revision's diagram, or "" (A28 (d)). */
  sldElementCode: string;
  unitRatings: GeneratorRatingDraft[];
};

// A choice the user has not made yet is held as "".
function chosenOrBlank<T extends z.ZodType<string>>(choice: T) {
  return z.union([z.literal(""), choice]);
}

// The shape a draft kept in the browser tab must still have to be restored: exactly the
// fields above, typed text as strings and the choices as values the contract knows or "".
// A draft saved by an older form fails here and is dropped instead of half-filling this one.
export const generatorStudyDraftSchema = z
  .object({
    code: z.string(),
    name: z.string(),
    steadyStateDemandKw: z.string(),
    steadyStatePowerFactor: z.string(),
    transientStepLoadKva: z.string(),
    transientAllowanceFactor: z.string(),
    futureGrowthFactor: z.string(),
    designMarginFactor: z.string(),
    ambientDeratingFactor: z.string(),
    altitudeDeratingFactor: z.string(),
    dutyUnits: z.string(),
    standbyUnits: z.string(),
    dutyClass: chosenOrBlank(generatorDutyClassSchema),
    redundancyMode: chosenOrBlank(generatorRedundancyModeSchema),
    scenario: chosenOrBlank(loadScenarioSchema),
    jurisdictionProfile: jurisdictionProfileSchema,
    notes: z.string(),
    // A draft kept before the field existed comes back as it was, with no source chosen.
    sldElementCode: z.string().default(""),
    unitRatings: z.array(z.object({ id: z.string(), value: z.string() }).strict()).min(1),
  })
  .strict();

let nextRatingNumber = 1;

// A prefix of its own, so a DG rating id is never mistaken for a Transformer one.
export function createGeneratorRatingId(): string {
  const id = `dg-rating-${nextRatingNumber}`;
  nextRatingNumber += 1;
  return id;
}

// Rating ids come from a counter that starts again at 1 on every page load, while a
// restored draft brings the ids it was saved with. Moving the counter past every id in use
// keeps an entry added afterwards from taking the id of one already on screen.
export function seedGeneratorRatingIdCounter(ids: readonly string[]): void {
  for (const id of ids) {
    const match = /^dg-rating-(\d+)$/.exec(id);
    if (match !== null) {
      nextRatingNumber = Math.max(nextRatingNumber, Number(match[1]) + 1);
    }
  }
}

export function createGeneratorRatingDraft(): GeneratorRatingDraft {
  return { id: createGeneratorRatingId(), value: "" };
}

// Only the defaults the backend itself has are filled in: one duty unit, no standby
// unit and the IN profile. Duty class, redundancy mode and scenario start unselected.
export function createInitialGeneratorStudyDraft(): GeneratorStudyDraft {
  return {
    code: "",
    name: "",
    steadyStateDemandKw: "",
    steadyStatePowerFactor: "",
    transientStepLoadKva: "",
    transientAllowanceFactor: "",
    futureGrowthFactor: "",
    designMarginFactor: "",
    ambientDeratingFactor: "",
    altitudeDeratingFactor: "",
    dutyUnits: "1",
    standbyUnits: "0",
    dutyClass: "",
    redundancyMode: "",
    scenario: "",
    jurisdictionProfile: "IN",
    notes: "",
    sldElementCode: "",
    unitRatings: [createGeneratorRatingDraft()],
  };
}

// An optional text or decimal field: sent trimmed, or left out when blank.
function optional(key: string, typed: string): Record<string, string> {
  const value = typed.trim();
  return value === "" ? {} : { [key]: value };
}

// A unit count is a whole number on the contract. A clean whole number is sent
// as one; anything else is passed through as typed, so the schema reports it
// instead of this function guessing what the engineer meant.
function countValue(typed: string): unknown {
  const value = typed.trim();
  return /^\d+$/.test(value) ? Number(value) : typed;
}

// The request body as the form will send it. It is validated afterwards with
// generatorRunCreateRequestSchema, so this function does not judge the values
// and never reorders the rating schedule. The standby count follows the shared
// EOS-03 redundancy rule (the backend schemas agree).
export function buildGeneratorRunPayload(
  draft: GeneratorStudyDraft,
  projectRevisionId?: string,
): Record<string, unknown> {
  return {
    study: {
      code: draft.code.trim(),
      name: draft.name.trim(),
      steady_state_demand_kw: draft.steadyStateDemandKw.trim(),
      steady_state_power_factor: draft.steadyStatePowerFactor.trim(),
      // Every entry is sent, blank ones as "", so the index in a message such as
      // "Unit rating 2 is required." always matches the entry's position on
      // screen. The schema refuses the blank; this builder does not hide it.
      available_unit_ratings_kva: draft.unitRatings.map((rating) => rating.value.trim()),
      // A17: blank = not established (left out); "0" = no step load (sent).
      ...optional("transient_step_load_kva", draft.transientStepLoadKva),
      ...optional("transient_allowance_factor", draft.transientAllowanceFactor),
      ...optional("future_growth_factor", draft.futureGrowthFactor),
      ...optional("design_margin_factor", draft.designMarginFactor),
      ...optional("ambient_derating_factor", draft.ambientDeratingFactor),
      ...optional("altitude_derating_factor", draft.altitudeDeratingFactor),
      duty_units: countValue(draft.dutyUnits),
      standby_units: countValue(deriveStandbyUnits(draft.redundancyMode, draft.dutyUnits)),
      ...optional("duty_class", draft.dutyClass),
      ...optional("redundancy_mode", draft.redundancyMode),
      ...optional("scenario", draft.scenario),
      ...optional("jurisdiction_profile", draft.jurisdictionProfile),
      ...optional("sld_element_code", draft.sldElementCode),
    },
    ...(projectRevisionId === undefined ? {} : { project_revision_id: projectRevisionId }),
    ...optional("notes", draft.notes),
  };
}

// On-screen labels of the generator study form, keyed by the request path.
// The run request nests the study under "study", so every study path carries
// that prefix; describeLocation numbers the schedule entries, giving
// "Unit rating 2 - ...". Shared factors carry the Transformer form's wording.
export const GENERATOR_STUDY_LABELS: ValidationLabels = {
  fields: {
    "study.code": "Study code",
    "study.name": "Study name",
    "study.sld_element_code": "Source from the diagram",
    "study.steady_state_demand_kw": "Steady-state demand (kW)",
    "study.steady_state_power_factor": "Power factor",
    "study.available_unit_ratings_kva": "Unit ratings",
    "study.transient_step_load_kva": "Step load (kVA)",
    "study.transient_allowance_factor": "Transient allowance factor",
    "study.future_growth_factor": "Future growth factor",
    "study.design_margin_factor": "Design margin factor",
    "study.ambient_derating_factor": "Ambient derating factor",
    "study.altitude_derating_factor": "Altitude derating factor",
    "study.duty_units": "Duty units",
    "study.standby_units": "Standby units",
    "study.duty_class": "Duty class",
    "study.redundancy_mode": "Redundancy mode",
    "study.scenario": "Scenario",
    "study.jurisdiction_profile": "Jurisdiction profile",
    notes: "Study notes",
  },
  // One entry of the rating schedule: "Unit rating 2".
  groups: { available_unit_ratings_kva: "Unit rating" },
};
